export interface RouteSeoInfo {
  readonly title: string;
  readonly description: string;
  readonly canonicalPath: string;
  readonly keywords: string;
  readonly ogImage: string;
}

export const SITE_URL = "https://pupil.rihonegroup.com";

export const DEFAULT_SEO: RouteSeoInfo = {
  title: "Pupil — Interactive 3D STEM Simulations & Visual Science Labs",
  description:
    "Explore live interactive 3D STEM simulations for astronomy and physics. Master Moon phases, Earth's 23.4° axial tilt and seasons, and solar altitude with visual orbital mechanics for students, teachers, and classrooms.",
  canonicalPath: "/",
  keywords:
    "moon phases simulation, interactive 3d astronomy, earth seasons simulation, axial tilt visualizer, solar altitude simulator, stem classroom demos, ngss astronomy models, lunar orbit interactive, visual science explanations, physics simulations, solstice equinox simulator, celestial horizon",
  ogImage: `${SITE_URL}/icons/pupil-og.svg`,
};

export const ROUTE_SEO: Record<string, RouteSeoInfo> = {
  "#/demos/moon-phases": {
    title:
      "Phases of the Moon — 3D Interactive Orbit & Earth View Simulation | Pupil",
    description:
      "Interactive 3D Moon phase simulation connecting sunlight angle, lunar orbit position, illuminated hemisphere, and the phase seen from Earth. Explore all 8 phases, tidal locking, and eclipse nodes.",
    canonicalPath: "/#/demos/moon-phases",
    keywords:
      "moon phases simulation, 3d lunar phase model, new moon full moon crescent gibbous, why does the moon have phases, lunar orbit simulation, tidal locking, eclipse nodes, middle school astronomy, ngss ms-ess1-1",
    ogImage: `${SITE_URL}/icons/pupil-og.svg`,
  },
  "#/demos/earth-seasons": {
    title:
      "Earth's Seasons & Axial Tilt — 3D Interactive Orbit Model | Pupil",
    description:
      "Interactive 3D simulation of Earth's 23.4° axial tilt, orbit eccentricity, solstices, equinoxes, and day-length variations by latitude. Understand why tilt, not distance, causes seasons.",
    canonicalPath: "/#/demos/earth-seasons",
    keywords:
      "earth seasons simulation, 23.4 axial tilt 3d model, june solstice december solstice, equinox simulation, why do we have seasons, solar insolation, polar day night, astronomy classroom simulation, ngss ms-ess1-1",
    ogImage: `${SITE_URL}/icons/pupil-og.svg`,
  },
  "#/demos/local-horizon": {
    title:
      "Local Horizon & Solar Altitude — Interactive Sky Simulator | Pupil",
    description:
      "Interactive first-person sky dome tracking the Sun's path, noon altitude, sunrise/sunset times, and day length across latitudes from the Equator to the Arctic Circle.",
    canonicalPath: "/#/demos/local-horizon",
    keywords:
      "solar altitude simulator, local horizon sky dome, sun path diagram interactive, solar elevation angle, azimuth and altitude, sunrise sunset daylight hours by latitude, celestial sphere simulator",
    ogImage: `${SITE_URL}/icons/pupil-og.svg`,
  },
};

export function getSeoForRoute(route: string): RouteSeoInfo {
  for (const [routePattern, seo] of Object.entries(ROUTE_SEO)) {
    if (route.startsWith(routePattern)) {
      return seo;
    }
  }
  return DEFAULT_SEO;
}

function setMetaTag(selector: string, attribute: string, value: string): void {
  if (typeof document === "undefined") {
    return;
  }
  let element = document.querySelector(selector) as HTMLMetaElement | null;
  if (element === null) {
    element = document.createElement("meta");
    const [attrName, attrVal] = selector
      .replace(/^meta\[/, "")
      .replace(/\]$/, "")
      .split("=");
    if (attrName && attrVal) {
      element.setAttribute(attrName, attrVal.replace(/['"]/g, ""));
    }
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
}

function setCanonical(href: string): void {
  if (typeof document === "undefined") {
    return;
  }
  let link = document.querySelector(
    "link[rel='canonical']",
  ) as HTMLLinkElement | null;
  if (link === null) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export function applySeoMetadata(route: string): RouteSeoInfo {
  const seo = getSeoForRoute(route);
  if (typeof document === "undefined") {
    return seo;
  }

  document.title = seo.title;
  setMetaTag("meta[name='description']", "content", seo.description);
  setMetaTag("meta[name='keywords']", "content", seo.keywords);
  setMetaTag("meta[property='og:title']", "content", seo.title);
  setMetaTag("meta[property='og:description']", "content", seo.description);
  setMetaTag("meta[property='og:image']", "content", seo.ogImage);
  setMetaTag(
    "meta[property='og:url']",
    "content",
    `${SITE_URL}${seo.canonicalPath}`,
  );
  setMetaTag("meta[name='twitter:title']", "content", seo.title);
  setMetaTag("meta[name='twitter:description']", "content", seo.description);
  setMetaTag("meta[name='twitter:image']", "content", seo.ogImage);
  setCanonical(`${SITE_URL}${seo.canonicalPath}`);

  return seo;
}
